import React, { useState, useCallback } from "react";
import moment from "moment";
import toast from "react-hot-toast";

import Icon from "./Icon";
import LiveRate from "./LiveRate";
import ProfitLoss from "./ProfitLoss";
import TimeTick from "./Time";
import Backend from "../../../../../Backend/Backend";

const backend = await new Backend().init();

export default function StandingCard({
  standing_delete,
  item,
  index,
  id,
  userType,
  reload,
  total,
  open,
}) {
  const [loading, setLoading] = useState(false);
  const [sqOffVisible, setSqOffVisible] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);

  const [tpassword, setTpassword] = useState("");

  // 🔹 Derived display values
  const exchangeName = (() => {
    switch (item.type) {
      case "nse":
        return "NSE";
      case "mcx":
        return "MCX";
      case "mcx_options":
        return "MCX-OPTIONS";
      case "fo":
        return "NSE-OPTIONS";
      case "nse_cash":
        return "NSE-EQUITY";
      case "comex_future":
        return "COMEX";
      case "crypto_future":
        return "CRYPTO";
      case "forex_future":
        return "FOREX";
      case "us_stock":
        return "US-STOCK";
      case "sgx":
        return "SGX";
      case "index":
        return "OTHERS";
      case "dgcx":
        return "DGCX";
      case "ncdex":
        return "NCDEX";
      default:
        return "--";
    }
  })();

  const expiryLabel =
    item.type === "nse_cash"
      ? "EQUITY"
      : moment(item.expiry_date).format("DD-MMM");

  // 🔹 Utility: safe rate formatting
  const formatRate = (rate) =>
    rate && !isNaN(rate)
      ? item.type === "forex_future"
        ? rate
        : parseFloat(rate)
      : 0;

  // 🔹 Event handlers
  const handleSqOff = useCallback(async () => {
    try {
      setLoading(true);
      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        all: 0,
        user_id: item.user_id,
        standing_id: item.id,
        tpassword: tpassword,
      };

      let res;
      if (userType === 1) res = await backend.sq_all_admin(data);
      else if (userType === 2) res = await backend.sq_all_super(data);
      else if (userType === 3) res = await backend.sq_all_master(data);
      else if (userType === 5) res = await backend.sq_all_broker(data);
      else if (userType === 6) res = await backend.sq_all_subbroker(data);

      setLoading(false);
      setSqOffVisible(false);

      if (res.error === "False") {
        toast.success(res.message);
        reload?.();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      setLoading(false);
      toast.error("Error while squaring off.");
      console.error(err);
    }
  }, [item, userType, reload, tpassword]);

  const handleDelete = useCallback(async () => {
    try {
      setLoading(true);
      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        delete_select: [item.id],
        tpassword: tpassword,
        type: "STANDING",
        admin: "1",
      };
      const res = await backend.delete(data);
      setLoading(false);
      setDeleteVisible(false);

      if (res.error === "False") {
        toast.success(res.message);
        reload?.();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      setLoading(false);
      toast.error("Error deleting standing.");
      console.error(err);
    }
  }, [item, reload, tpassword]);

  // 🔹 User edit handlers

  const handleOpenEdit = (type) => {
    if (!item) return;
    switch (type) {
      case "super":
        window.user_id = item.super_id;
        window.openeditSuper?.();
        break;
      case "master":
        window.user_id = item.master_id;
        window.masterModalOpen?.();
        break;
      case "user":
        window.user_id = item.user_id;
        window.userModalOpen?.();
        break;
      default:
        break;
    }
  };

  return (
    <li
      key={item.id}
      className={`menuContainer addHight text ${
        id === index ? "selection" : ""
      }`}
    >
      <div className="menuIcon">
        <Icon symbol_id={item.symbol_id} item={item} />
      </div>
      <div className="menuExchange small">{exchangeName}</div>
      {userType === 1 && (
        <div onClick={() => handleOpenEdit("super")} className="menuExchange">
          <div className="user_id">{item.super_username}</div>
        </div>
      )}
      {(userType === 1 || userType === 2) && (
        <div onClick={() => handleOpenEdit("master")} className="menuExchange">
          <div className="user_id">{item.master_username}</div>
        </div>
      )}
      {(userType === 1 || userType === 2 || userType === 3 || userType === 5) && (
        <div onClick={() => handleOpenEdit("user")} className="menuExchange">
          <div className="user_id">{item.username}</div>
        </div>
      )}
      <div className={`menuSymbol ${item.type === "fo" ? "small" : null} `}>
        {item.symbol}
      </div>
      <div className="menuExpiry">{expiryLabel}</div>
      <div className="menuBuyPrice">
        <div
          className={`menuBuyPrice ${
            item.buy_sell === 0 ? "buytext" : "selltext"
          }`}
        >
          {item.buy_sell === 0 ? "BUY" : "SELL"}
        </div>
      </div>
      <div className="menuBuyPrice">{item.qty}</div>
      <div className="menuBuyPrice">{formatRate(item.rate)}</div>
      <div className="menuBuyPrice">
        <LiveRate symbol_id={item.symbol_id} buy_sell={item.buy_sell} />
      </div>
      <ProfitLoss
        symbol_id={item.symbol_id}
        item={item}
        total={total}
        userType={userType}
      />
      <div className="menuLtt extraSmall">
        <TimeTick symbol_id={item.symbol_id} />
      </div>
      {(userType === 3 || userType === 4) && (
        <div className="menuLtt flex flex-col items-center justify-center">
          <div className="extraSmall">
            {moment(item.date_created).format("DD-MM-YYYY")}
          </div>
          <div className="small">
            {moment(item.date_created).format("HH:mm:ss")}
          </div>
        </div>
      )}
      {(userType === 3 || userType === 4) && (
        <div className="menuLtt flex flex-col items-center justify-center">
          <div className="extraSmall">
            {moment(item.date_update).format("DD-MM-YYYY")}
          </div>
          <div className="small">
            {moment(item.date_update).format("HH:mm:ss")}
          </div>
        </div>
      )}
      {/* SQ-OFF Button */}

      {userType === 4 ? (
        <div className="menuBuyPrice">
          <button
            onClick={() => open(item, item.buy_sell === 0 ? 1 : 0)}
            className={`${"bg-[var(--sell)] text-[var(--white)]"} mr-1 px-2.5 py-1 text-[12px] relative flex items-center justify-center gap-1 font-semibold uppercase rounded-md transition-all duration-300 cursor-pointer border-2 border-[var(--hover)] shadow-[0_0_6px_rgba(255,255,255,0.1)]`}
          >
            SQ-OFF
          </button>
        </div>
      ) : (
        <div onClick={() => setSqOffVisible(true)} className="menuBuyPrice">
          <button
            className={`${"bg-[var(--sell)] text-[var(--white)]"} mr-1 px-2.5 py-1 text-[12px] relative flex items-center justify-center gap-1 font-semibold uppercase rounded-md transition-all duration-300 cursor-pointer border-2 border-[var(--hover)] shadow-[0_0_6px_rgba(255,255,255,0.1)]`}
          >
            SQ-OFF
          </button>
        </div>
      )}

      {/* DELETE Button */}
      {standing_delete === 1 && (
        <div onClick={() => setDeleteVisible(true)} className="menuBuyPrice">
          <button
            className={`${"bg-[var(--sell)] text-[var(--white)]"} mr-1 px-2.5 py-1 text-[12px] relative flex items-center justify-center gap-1 font-semibold uppercase rounded-md transition-all duration-300 cursor-pointer border-2 border-[var(--hover)] shadow-[0_0_6px_rgba(255,255,255,0.1)]`}
          >
            DELETE
          </button>
        </div>
      )}

      {/* 🔹 SqOff Modal */}
      {sqOffVisible && (
        <div
          className="customModalOverlay"
          onClick={() => setSqOffVisible(false)}
        >
          <div className="customModal" onClick={(e) => e.stopPropagation()}>
            <h3 className="customModalTitle">⚠️ Sq.Off Standing</h3>
            <p className="customModalWarning">
              This action will sq.off <b>{item.username}</b> standing{" "}
              <b>{item.symbol}</b>.
            </p>

            <input
              type="password"
              placeholder="Enter Transaction password"
              value={tpassword}
              onChange={(e) => setTpassword(e.target.value)}
              disabled={loading}
              className="customInput"
            />

            <div className="customModalButtons">
              <button
                className="dropbtnNavSub white sell"
                onClick={() => setSqOffVisible(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="dropbtnNavSub white primary"
                onClick={handleSqOff}
                disabled={loading}
              >
                {loading ? "SQ.OFF..." : "Confirm Sq.Off"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔹 Delete Modal */}
      {deleteVisible && (
        <div
          className="customModalOverlay"
          onClick={() => setDeleteVisible(false)}
        >
          <div className="customModal" onClick={(e) => e.stopPropagation()}>
            <h3 className="customModalTitle">⚠️ Delete Standing</h3>
            <p className="customModalWarning">
              This action will delete user <b>{item.username}</b> standing{" "}
              <b>{item.symbol}</b> after backup.
            </p>

            <input
              type="password"
              placeholder="Enter Transaction password"
              value={tpassword}
              onChange={(e) => setTpassword(e.target.value)}
              disabled={loading}
              className="customInput"
            />

            <div className="customModalButtons">
              <button
                className="dropbtnNavSub white sell"
                onClick={() => setDeleteVisible(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="dropbtnNavSub white primary"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}
